import { useMemo } from "react";

import { Background } from "./Background";
import { Person } from "./Person";
import { TreeSmall } from "./TreeSmall";
import { TreeMedium } from "./TreeMedium";
import { TreeLarge } from "./TreeLarge";

export interface TreeDiagramProps {
  label: string;
  treeDiameter: number;
  treeDistance?: number;
}

export function TreeDiagram({
  label,
  treeDiameter,
  treeDistance,
}: TreeDiagramProps) {
  const lineWidth = useMemo(() => {
    if (treeDiameter <= 0.15) {
      return 5;
    }
    if (treeDiameter <= 0.2) {
      return 5;
    }
    if (treeDiameter <= 0.3) {
      return 7;
    }
    if (treeDiameter <= 0.4) {
      return 10;
    }
    if (treeDiameter <= 0.5) {
      return 15;
    }
    if (treeDiameter <= 0.6) {
      return 20;
    }
    if (treeDiameter <= 0.7) {
      return 25;
    }
    if (treeDiameter <= 0.8) {
      return 30;
    }
    if (treeDiameter <= 0.9) {
      return 30;
    }
    if (treeDiameter <= 1.0) {
      return 35;
    }
    if (treeDiameter <= 1.1) {
      return 40;
    }
    if (treeDiameter <= 1.2) {
      return 40;
    }
    if (treeDiameter <= 1.3) {
      return 45;
    }
    if (treeDiameter <= 1.4) {
      return 48;
    }
    if (treeDiameter <= 1.5) {
      return 50;
    }
  }, [treeDiameter]);

  const treeMarginLeft = useMemo(() => {
    if (treeDiameter <= 0.1) {
      return "mr-[-14px]";
    }
    if (treeDiameter <= 0.1) {
      return "mr-[-15px]";
    }
    if (treeDiameter <= 0.2) {
      return "mr-[-15px]";
    }
    if (treeDiameter <= 0.3) {
      return "mr-[-16px]";
    }
    if (treeDiameter <= 0.4) {
      return "mr-[-18px]";
    }
    if (treeDiameter <= 0.5) {
      return "mr-[-10px]";
    }
    if (treeDiameter <= 0.6) {
      return "mr-[-10px]";
    }
    if (treeDiameter <= 0.7) {
      return "mr-[-10px]";
    }
    if (treeDiameter <= 0.8) {
      return "mr-[-10px]";
    }
    if (treeDiameter <= 0.9) {
      return "mr-[-10px]";
    }
    if (treeDiameter <= 0.95) {
      return "mr-[-10px]";
    }
  }, [treeDiameter]);

  const treeMarginRight = useMemo(() => {
    if (treeDiameter <= 1.05) {
      return "ml-[-32px]";
    }
    if (treeDiameter <= 1.1) {
      return "ml-[-33px]";
    }
    if (treeDiameter <= 1.2) {
      return "ml-[-35px]";
    }
    if (treeDiameter <= 1.3) {
      return "ml-[-37px]";
    }
    if (treeDiameter <= 1.4) {
      return "ml-[-40px]";
    }
    if (treeDiameter <= 1.5) {
      return "ml-[-42px]";
    }
  }, [treeDiameter]);

  const treeSize = (treeDiameter + 0.6) * 250;

  const treeDistanceStyles = useMemo(() => {
    return {
      marginLeft: treeDistance ? `${treeDistance * 40}px` : 0,
    };
  }, [treeDistance]);

  return (
    <div className="relative z-0 flex h-[300px] w-full flex-col overflow-hidden">
      <Background className="w-full grow" />

      <div className="ml-15 absolute bottom-0 left-0 right-0 top-0 z-10 flex items-end justify-start">
        <div className="grow-[2]" />
        <Person className="mb-6" style={{ transform: "scale(1.1, 1.1)" }} />
        <div className="grow-[8]" />
      </div>

      <div
        className="relative bottom-0 left-0 right-0 top-0 z-10 flex flex-col items-center"
        style={treeDistanceStyles}
      >
        {treeDiameter <= 0.4 && (
          <TreeSmall
            className={`absolute bottom-[19px] ${treeMarginLeft}`}
            style={{
              width: `${treeSize}px`,
              height: `${treeSize}px`,
            }}
          />
        )}
        {treeDiameter > 0.4 && treeDiameter <= 0.95 && (
          <TreeMedium
            className={`absolute bottom-[19px] ${treeMarginLeft}`}
            style={{
              width: `${treeSize}px`,
              height: `${treeSize}px`,
            }}
          />
        )}
        {treeDiameter > 0.95 && (
          <TreeLarge
            className={`absolute bottom-[19px] ${treeMarginRight}`}
            style={{
              width: `${treeSize}px`,
              height: `${treeSize}px`,
            }}
          />
        )}
      </div>

      <div
        className="relative bottom-0 left-0 right-0 top-0 z-50 flex flex-col items-center"
        style={treeDistanceStyles}
      >
        <div className="absolute bottom-[64px] flex flex-col items-center gap-2">
          <div className="flex items-center">
            <div className="bg-textPrimary h-2 w-[1px]" />
            <div
              className="border-textPrimary h-[1px] border-t border-dashed"
              style={{ width: `${lineWidth}px` }}
            />
            <div className="bg-textPrimary h-2 w-[1px]" />
          </div>
          <div className="relative flex h-[1px] w-[1px] justify-center">
            <p className="bg-interfaceWhite text-textPrimary text-bodymd absolute rounded px-1">
              {label}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
